import { keepPreviousData, useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./client";
import { fetchAllPages } from "./fetchAllPages";
import { getActingTenantId } from "../auth/session";
import type {
  Firmware,
  CreateFirmwareResponse,
  DownloadResponse,
  ListResponse,
  SingleResponse,
} from "./types";

export type FirmwareSearchField = "filename" | "version";

interface ListFirmwareParams {
  limit?: number;
  cursor?: string;
  status?: string;
  tenantId?: string;
  // Case-insensitive substring on searchField (filename by default),
  // applied by the server across every page.
  search?: string;
  searchField?: FirmwareSearchField;
  // Load every page — for pickers that must offer the whole (bounded)
  // list rather than its first page. Ignores limit/cursor.
  allPages?: boolean;
}

export function useFirmwareList(params: ListFirmwareParams = {}) {
  const actingTenantId = getActingTenantId();
  const scopeTenantId = params.tenantId ?? actingTenantId ?? undefined;
  const qs = new URLSearchParams();
  if (params.limit && !params.allPages) qs.set("limit", String(params.limit));
  if (params.cursor && !params.allPages) qs.set("cursor", params.cursor);
  if (params.status) qs.set("status", params.status);
  const search = params.search?.trim();
  if (search) {
    qs.set("search", search);
    qs.set("searchField", params.searchField ?? "filename");
  }
  const query = qs.toString();
  const path = `/api/v1/firmware${query ? `?${query}` : ""}`;
  const init = { headers: scopeTenantId ? { "X-Acting-Tenant-ID": scopeTenantId } : undefined };

  return useQuery({
    queryKey: ["firmware", scopeTenantId, params],
    queryFn: () => (params.allPages ? fetchAllPages<Firmware>(path, init) : apiFetch<ListResponse<Firmware>>(path, init)),
    placeholderData: keepPreviousData,
  });
}

export function useFirmware(id: string, tenantId?: string) {
  const actingTenantId = getActingTenantId();
  const scopeTenantId = tenantId ?? actingTenantId ?? undefined;
  return useQuery({
    queryKey: ["firmware", scopeTenantId, id],
    queryFn: () => apiFetch<SingleResponse<Firmware>>(`/api/v1/firmware/${id}`, {
      headers: scopeTenantId ? { "X-Acting-Tenant-ID": scopeTenantId } : undefined,
    }),
    enabled: !!id,
  });
}

export function useCreateFirmware(tenantId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      version: string;
      fileName: string;
      fileSize: number;
      contentType?: string;
      description?: string;
      model: string;
    }) =>
      apiFetch<SingleResponse<CreateFirmwareResponse>>("/api/v1/firmware", {
        method: "POST",
        headers: tenantId ? { "X-Acting-Tenant-ID": tenantId } : undefined,
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["firmware"] }),
  });
}

export function useActivateFirmware(tenantId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, checksum }: { id: string; checksum: string }) =>
      apiFetch<SingleResponse<Firmware>>(`/api/v1/firmware/${id}`, {
        method: "PUT",
        headers: tenantId ? { "X-Acting-Tenant-ID": tenantId } : undefined,
        body: JSON.stringify({ status: "active", checksum }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["firmware"] }),
  });
}

export function useUpdateFirmware(tenantId?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: {
      description?: string;
      status?: string;
      checksum?: string;
      version?: string;
      fileName?: string;
      fileSize?: number;
      contentType?: string;
      model?: string;
    } }) =>
      apiFetch<SingleResponse<Firmware>>(`/api/v1/firmware/${id}`, {
        method: "PUT",
        headers: tenantId ? { "X-Acting-Tenant-ID": tenantId } : undefined,
        body: JSON.stringify(body),
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["firmware"] });
      qc.invalidateQueries({ queryKey: ["firmware", vars.id] });
    },
  });
}

export function useDeleteFirmware() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/v1/firmware/${id}`, { method: "DELETE" }),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ["firmware"] });
      qc.removeQueries({ queryKey: ["firmware", id] });
    },
  });
}

export function useFirmwareDownload(id: string) {
  const actingTenantId = getActingTenantId();
  return useQuery({
    queryKey: ["firmware-download", actingTenantId, id],
    queryFn: () =>
      apiFetch<SingleResponse<DownloadResponse>>(`/api/v1/firmware/${id}/download`),
    enabled: false,
  });
}

export async function uploadToS3(url: string, file: Blob): Promise<void> {
  const res = await fetch(url, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type || "application/octet-stream" },
  });
  if (!res.ok) {
    throw new Error(`S3 upload failed: ${res.status}`);
  }
}

export async function computeSHA256(file: Blob): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hash = await crypto.subtle.digest("SHA-256", buffer);
  const bytes = new Uint8Array(hash);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}
